<template>
  <v-navigation-drawer
    v-model="isSelected"
    @transitionend="trans"
    class="member-drawer"
    absolute
    floating
    temporary
    right
    width="500px"
  >
    <v-toolbar class="member-toolbar" color="primary" absolute v-if="selectedMember">
      <v-toolbar-title>
        {{selectedMember.first_name}} {{selectedMember.last_name}}
        <span class="subtitle-2"></span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="confirmVerify = true" :title="member.verified ? 'Unverify Member' : 'Verify Member'">
        <v-icon color>{{member.verified ? 'mdi-account-check':'mdi-account-alert'}}</v-icon>
      </v-btn>
      <v-btn icon @click="confirmBan = true" :title="member.banned ? 'Unban Member' : 'Ban Member'">
        <v-icon color>{{member.banned ? 'mdi-account-cancel':'mdi-account-heart'}}</v-icon>
      </v-btn>
      <v-btn icon @click="confirmRemove = true" title="Delete">
        <v-icon color>mdi-delete</v-icon>
      </v-btn>
      <v-btn icon color="dark" @click="close()" title="Close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card class="member-card" :rounded="false" flat v-if="selectedMember">
      <Confirm
        :confirm.sync="confirmBan"
        :callback="toggleBanned"
        :message="`Are you sure you want to <b>${member.banned ? 'unban':'ban'}</b> this member? Banned members will be removed from groups and not be allowed to rejoin (until they're unbanned).`"
      ></Confirm>
      <Confirm
        :confirm.sync="confirmRemove"
        :callback="remove"
        message="Are you sure you want to remove this member? Removed members will be able to register with your bot again."
      ></Confirm>
      <Confirm
        :confirm.sync="confirmVerify"
        :callback="verified"
        message="Are you sure you want to change this member's verified status?"
      ></Confirm>
      <div class="mt-4 px-4">
        <v-select
          v-model="selectedMember.status"
          :items="memberStatusses"
          label="Member Status"
          placeholde="Set Status..."
          @change="setStatus()"
          clearable
        ></v-select>
      </div>

      <v-list-item two-line v-if="selectedMember.cell || selectedMember.email">
        <v-list-item-content>
          <v-list-item-title
            v-if="!selectedMember.verified"
            class="subtitle mb-4"
          >{{selectedMember.first_name}} is an <u>unverified</u> member!</v-list-item-title>
          <v-list-item-title class="mb-3" v-if="selectedMember.cell">
            <v-icon color="accent" class="mr-2">mdi-phone</v-icon>
            <a :href="`tel:${selectedMember.cell}`">{{selectedMember.cell}}</a>
          </v-list-item-title>
          <v-list-item-title class="mb-2" v-if="selectedMember.email">
            <v-icon color="accent" class="mr-2">mdi-at</v-icon>
            <a :href="`mailto:${selectedMember.email}`">{{selectedMember.email}}</a>
          </v-list-item-title>
        </v-list-item-content>

        <v-list-item-avatar size="80" color="grey" v-if="profilePhoto">
          <v-img :src="`${filesUrl}/${profilePhoto.id}?access_token=${accessToken}`">
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="accent"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </v-list-item-avatar>
      </v-list-item>

      <v-divider></v-divider>

      <v-subheader v-if="memberGroups && memberGroups.length">Joined Groups</v-subheader>
      <p class="px-4"><small>Groups that this member has joined (click group to ban/unban member from the group).</small></p>
      <v-row v-if="memberGroups && memberGroups.length">
        <v-col v-for="memberGroup in memberGroups" :key="memberGroup.id" cols="12" class="px-6 py-1">
          <v-chip color="accent" class="mx-2" link @click="memberGroupEdit = memberGroup">
            <v-icon
              class="mr-1"
              :color="memberGroup.banned ? 'red': (memberGroup.in_group ? 'green':'orange')"
            >{{memberGroup.banned ? 'mdi-account-cancel': (memberGroup.in_group ? 'mdi-account-check':'mdi-account-remove')}}</v-icon>
            {{memberGroup.group.title}}
          </v-chip>
        </v-col>
      </v-row>

      <v-row v-if="memberGroupEdit">
        <v-col cols="12" class="px-6 py-1 mt-4">
          <v-card flat>
            <v-card-title class="flex justify-space-between align-center subtitle-1">
              <div>{{ memberGroupEdit.group.title }} <span class="body-2 ml-2">Settings</span></div>
              <v-btn
                class="mt-n2"
                icon
                dark
                x-small
                @click="memberGroupEdit = null"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="my-0 py-0">
              <div class="px-3 body-2">
                <p v-if="!memberGroupEdit.in_group">{{ selectedMember.first_name }} joined the group <u>{{ memberGroupEdit.group.title }}</u> but has subsequently left or was banned from the group.</p>
                <p v-else>{{ selectedMember.first_name }} joined the group <u>{{ memberGroupEdit.group.title }}</u> and is currently still in the group.</p>
                <ul class="date-list">
                  <li v-if="memberGroupEdit.join_date">Joined on: {{ memberGroupEdit.join_date | date_only }}</li>
                  <li v-if="memberGroupEdit.leave_date">Left on: {{ memberGroupEdit.leave_date | date_only }}</li>
                  <li v-if="memberGroupEdit.kick_date">Banned on: {{ memberGroupEdit.kick_date | date_only }}</li>
                </ul>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="!memberGroupEdit.banned"
                color="primary"
                text
                @click="groupBan(true)"
              >
                Ban
              </v-btn>

              <v-btn
                v-if="memberGroupEdit.banned"
                color="primary"
                text
                @click="groupBan(false)"
              >
                Unban
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <v-subheader class="mt-2">Allow Joining Other Groups</v-subheader>
      <v-row>
        <v-col cols="12" class="px-6 py-0">
          <small>Similar to white-listing files for groups, override the groups that a member is allowed to join here.</small>
          <v-select
            v-model="selectedMember.whitelisted_groups"
            :items="whitelistGroups"
            attach
            item-value="id"
            item-text="title"
            label="Whitelisted Group/s"
            hint="You can select multiple groups."
            class="py-2"
            :class="{ 'mt-6': selectedMember.whitelisted_groups.length }"
            persistent-hint
            validate-on-blur
            multiple
            @change="saveMember()"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 2"
                class="font-weight-bold"
              >
                <span>{{ item.title }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text caption">(+{{ selectedMember.whitelisted_groups.length - 2 }} others)</span
              >
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-divider class="py-2 mt-4"></v-divider>

      <v-subheader v-if="selectedMember.physical_address || selectedMember.postal_address">Address</v-subheader>
      <v-list-item two-line v-if="selectedMember.physical_address">
        <v-list-item-icon>
          <v-icon color="accent">mdi-map</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 grey--text">Physical</v-list-item-title>
          <p class="body-2" v-html="physicalAddress"></p>
        </v-list-item-content>
      </v-list-item>
      <v-list-item two-line v-if="selectedMember.location">
        <v-list-item-icon>
          <v-icon color="accent">mdi-map-marker</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 grey--text">Coordinates</v-list-item-title>
          <p class="body-2">
            Latitude: {{selectedMember.location.latitude}}<br>
            Longitude: {{selectedMember.location.longitude}}
          </p>
        </v-list-item-content>
      </v-list-item>

      <v-list-item class="mt-n6" two-line v-if="selectedMember.postal_address">
        <v-list-item-icon>
          <v-icon color="accent">mdi-post</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 grey--text">Postal</v-list-item-title>
          <p class="body-2" v-html="postalAddress"></p>
        </v-list-item-content>
      </v-list-item>

      <v-subheader v-if="selectedMember.id_number || selectedMember.passport_number">Identification</v-subheader>
      <v-list-item two-line v-if="selectedMember.id_number">
        <v-list-item-icon>
          <v-icon color="accent">mdi-smart-card</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 grey--text">ID</v-list-item-title>
          <p class="body-2">{{selectedMember.id_number || 'none'}}</p>
        </v-list-item-content>
      </v-list-item>

      <v-list-item class="mt-n6" two-line v-if="selectedMember.passport_number">
        <v-list-item-icon></v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 grey--text">Passport</v-list-item-title>
          <p class="body-2">{{selectedMember.passport_number || 'none'}}</p>
        </v-list-item-content>
      </v-list-item>

      <v-list v-if="Object.keys(selectedMember.custom).length">
        <v-subheader>Custom</v-subheader>
        <v-list-item class="mt-n6" two-line v-for="(value, key) in selectedMember.custom" :key="key">
          <v-list-item-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="subtitle-1 grey--text">{{key | title_case}}</v-list-item-title>
            <p class="body-2">{{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}}</p>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { makeGetMixin, makeFindMixin, models } from "feathers-vuex";
import Confirm from "@/components/widget/Confirm";

export default {
  data() {
    return {
      loading: false,
      confirmBan: false,
      confirmRemove: false,
      confirmVerify: false,
      isOpen: false,
      memberGroupEdit: null,
    };
  },
  mixins: [
    makeGetMixin({
      service: "members",
      name: "selectedMember",
      id: "memberId",
    }),
    makeFindMixin({
      service: "groups",
      name: "whitelistGroups",
    }),
  ],
  created() {},
  mounted() {},
  computed: {
    ...mapGetters("config", ["botApiUrl"]),
    ...mapState("apiAuth", ["accessToken"]),
    filesUrl() {
      return new URL(
        process.env.VUE_APP_FILES_PATH,
        this.botApiUrl || process.env.VUE_APP_API_URL
      ).toString();
    },
    memberId() {
      return this.member && this.member.id;
    },
    memberGroups() {
      return this.selectedMember && this.selectedMember.member_groups;
    },
    selectedMemberParams() {
      return {
        query: {
          $eager: "[member_groups.[group], files]",
          $modifyEager: { files: { for: "profilePhoto" } },
        },
      };
    },
    whitelistGroupsParams() {
      return {
        query: {
          deleted: false,
          enabled: true
        },
      };
    },
    physicalAddress() {
      return this.selectedMember.physical_address.split("\n").join("<br>");
    },
    postalAddress() {
      return this.selectedMember.postal_address.split("\n").join("<br>");
    },
    profilePhoto() {
      return (
        this.selectedMember &&
        this.selectedMember.files &&
        this.selectedMember.files.find((file) => file.for === "profilePhoto")
      );
    },
    isSelected: {
      get() {
        return !!(this.member && this.member.id);
      },
      set(open) {
        this.isOpen = open;
      },
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    trans() {
      if (!this.isOpen) this.close();
    },
    close() {
      this.$emit("update:member", null);
      this.$emit("on-close");
      this.memberGroupEdit = null;
    },
    setStatus() {
      if (this.selectedMember.status === undefined) this.selectedMember.status = null;

      this.patchMember({ status: this.selectedMember.status });
    },
    async groupBan(banned) {
      if (typeof this.memberGroupEdit.patch !== "function") {
        const { GroupMembers } = models.bot;
        this.memberGroupEdit = new GroupMembers(this.memberGroupEdit);
      }

      const index = this.memberGroups.findIndex(
        (group) => group.id === this.memberGroupEdit.id
      );

      try {
        await this.memberGroupEdit.patch({
          data: {
            banned: banned,
          },
        });

        this.memberGroups.splice(index, 1, this.memberGroupEdit);
      } catch (error) {
        this.showMessage({
          text: error.message,
          color: "warning",
        });
        console.log("Failed to ban group member...");
      }
    },
    toggleBanned() {
      const isBanned =
        typeof this.member.banned === "boolean" ? !this.member.banned : true;
      try {
        this.member.patch({
          data: {
            banned: isBanned,
          },
        });

        if (isBanned) {
          this.showMessage({
            text:
              "Member has been banned and is being kicked from allowed groups!",
            color: "warning",
          });
        } else {
          this.showMessage({
            text: "Member has been unbanned and can rejoin allowed groups!",
            color: "success",
          });
        }
      } catch (error) {
        console.log("Failed to ban member...");
      }
    },
    remove() {
      this.member.remove();
      this.$emit("update:removed", true);
      this.close();
    },
    async patchMember(data) {
      await this.selectedMember.patch({
        data: data,
      });
    },
    async saveMember() {
      try {
        await this.selectedMember.save();

        this.showMessage({
          text:
            "Saved member changes.",
          color: "success",
        });
      } catch(error) {
        this.showMessage({
          text:
            "Failed to save member changes, please try again.",
          color: "warning",
        });
      }
    },
    verified() {
      const isVerified =
        typeof this.member.verified === "boolean" ? !this.member.verified : true;

      try {
        this.member.patch({
          data: {
            verified: isVerified,
          },
        });
      } catch (error) {
        console.log("Failed to verify member...");
      }
    }
  },
  watch: {},
  components: {
    Confirm,
  },
  props: {
    member: {
      type: Object,
      default: () => {
        return {};
      },
    },
    memberStatusses: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.member-card {
  background: none;
}

.member-toolbar {
  width: 100%;
}

.member-drawer {
  z-index: 9;
}

.member-container,
.member-card {
  padding-top: 64px;

  .newlines {
    white-space: pre;
  }

  .image-grid-block {
    position: relative;
    cursor: pointer;

    .image-magnify {
      position: absolute;
      bottom: 0;
      right: 0;
      margin: 0 15px 15px 0;
      z-index: 10;
    }
  }
}

.date-list {
  list-style-type: circle;
  list-style-position: outside;
}
</style>